import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const AYLAR = ['','Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

const KONU_AGACI = [
  { id: 'hayvancilik', ad: 'Hayvancılık', ikon: '🐄', children: [
    { id: 'buyukbas', ad: 'Büyükbaş', ikon: '🐄' },
    { id: 'kucukbas', ad: 'Küçükbaş', ikon: '🐑' },
    { id: 'at', ad: 'At Yetiştiriciliği', ikon: '🐎' },
    { id: 'ari', ad: 'Arıcılık', ikon: '🐝' },
    { id: 'ceylan', ad: 'Asya Ceylanı', ikon: '🦌' },
    { id: 'kopek', ad: 'Çoban Köpeği', ikon: '🐕' },
  ]},
  { id: 'bitkisel', ad: 'Bitkisel Üretim', ikon: '🌾', children: [
    { id: 'tarla', ad: 'Tarla Bitkileri', ikon: '🌾' },
    { id: 'yem', ad: 'Yem Bitkileri', ikon: '🌿' },
    { id: 'bahce', ad: 'Bahçe Kültürleri', ikon: '🌳' },
  ]},
  { id: 'varlik', ad: 'Varlık', ikon: '🏗️', children: [
    { id: 'arazi', ad: 'Arazi Varlığı', ikon: '🌱' },
    { id: 'makine', ad: 'Makine & Ekipman', ikon: '🚜' },
    { id: 'tasit', ad: 'Taşıt', ikon: '🚗' },
    { id: 'tesis', ad: 'Tesis', ikon: '🏗️' },
  ]},
];

const IRK_LISTESI: Record<string,string[]> = {
  buyukbas: ['Holstein (Siyah Alaca)','Esmer (Brown Swiss)','Simmental','Montofon','Angus'],
  kucukbas: ['Merinos','Akkaraman','Morkaraman','İvesi','Kıvırcık','Halep','Ankara Tiftik'],
  tarla: ['Buğday - Ekmeklik','Buğday - Makarnalık','Arpa','Tritikale','Mısır','Ayçiçeği','Nohut','Mercimek'],
};

export default function Raporlama() {
  const [isletmeler, setIsletmeler] = useState<any[]>([]);
  const [secilenIsletmeler, setSecilenIsletmeler] = useState<number[]>([]);
  const [tumIsletmeler, setTumIsletmeler] = useState(true);
  const [yillar, setYillar] = useState<number[]>([new Date().getFullYear() - 1]);
  const [ay, setAy] = useState(0);
  const [secilenKonular, setSecilenKonular] = useState<string[]>([]);
  const [secilenIrklar, setSecilenIrklar] = useState<string[]>([]);
  const [acikDallar, setAcikDallar] = useState<string[]>(['hayvancilik','bitkisel','varlik']);
  const [kiyaslama, setKiyaslama] = useState(false);
  const [rapor, setRapor] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [irkArama, setIrkArama] = useState('');
  const raporRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    axios.get(`${API}/api/rapor/isletmeler`, { headers: headers() }).then(r => setIsletmeler(r.data));
  }, []);

  // Kriterler değişince otomatik rapor hazırla (800ms debounce)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      raporOlusturAuto();
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [yillar, ay, secilenKonular, secilenIrklar, secilenIsletmeler, tumIsletmeler, kiyaslama]);

  const toggleIsletme = (id: number) => {
    setTumIsletmeler(false);
    setSecilenIsletmeler(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleKonu = (id: string, anaId?: string) => {
    setSecilenKonular(prev => {
      if (prev.includes(id)) {
        const node = KONU_AGACI.find(k => k.id === id);
        const altIds = node ? node.children.map((c: any) => c.id) : [];
        return prev.filter(x => x !== id && !altIds.includes(x));
      }
      return [...prev, id];
    });
  };

  const toggleYil = (yil: number) => {
    setYillar(prev => prev.includes(yil) ? prev.filter(y => y !== yil) : [...prev, yil].sort());
  };

  const toggleIrk = (irk: string) => {
    setSecilenIrklar(prev => prev.includes(irk) ? prev.filter(x => x !== irk) : [...prev, irk]);
  };

  const _fetchRapor = async (silent = false) => {
    if (!silent) setRapor(null);
    setYukleniyor(true);
    try {
      const res = await axios.post(`${API}/api/rapor/olustur`, {
        isletme_ids: tumIsletmeler ? [] : secilenIsletmeler,
        yillar, ay: ay || null,
        konular: secilenKonular,
        irklar: secilenIrklar,
        kiyaslama_modu: kiyaslama && yillar.length > 1,
      }, { headers: headers() });
      setRapor(res.data);
    } catch(e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  const raporOlustur = () => _fetchRapor(false);
  const raporOlusturAuto = () => _fetchRapor(true);

  const excelIndir = () => {
    if (!rapor) return;
    const wb = XLSX.utils.book_new();

    // Her b\u00F6l\u00FCm ayr\u0131 bir sayfa olarak
    rapor.bolumler.forEach((bolum: any, bi: number) => {
      const wsData: any[][] = [];

      // Ba\u015Fl\u0131k sat\u0131r\u0131
      wsData.push([bolum.baslik]);
      wsData.push([]);

      // S\u00FCtun ba\u015Fl\u0131klar\u0131
      wsData.push(bolum.sutunlar);

      // Veri sat\u0131rlar\u0131
      bolum.satirlar.forEach((satir: any) => {
        wsData.push(Object.values(satir));
      });

      // Toplam sat\u0131r\u0131 (birden fazla sat\u0131r varsa)
      if (bolum.satirlar.length > 1) {
        const totRow: any[] = ['TOPLAM'];
        for (let si = 1; si < bolum.sutunlar.length; si++) {
          const vals = bolum.satirlar.map((s: any) => Object.values(s)[si]);
          const sum = vals.reduce((a: any, b: any) => typeof b === 'number' ? (a as number) + b : a, 0);
          totRow.push(typeof vals[0] === 'number' ? sum : '');
        }
        wsData.push(totRow);
      }

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // S\u00FCtun geni\u015Fliklerini ayarla
      ws['!cols'] = bolum.sutunlar.map((_: any, i: number) => ({
        wch: i === 0 ? 28 : 16
      }));

      // Sayfa ad\u0131 (max 31 karakter, ge\u00E7ersiz karakter yok)
      let sheetName = bolum.baslik
        .replace(/[:\\\/\[\]\*\?]/g, '')
        .substring(0, 31)
        .trim();
      if (!sheetName) sheetName = `Bolum${bi + 1}`;

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const donem = rapor.ay ? `${AYLAR[rapor.ay]}_${rapor.yillar.join('-')}` : rapor.yillar.join('-');
    XLSX.writeFile(wb, `TIGEM_Rapor_${donem}.xlsx`);
  };

  const pdfIndir = () => {
    document.body.classList.add('rapor-yazdir-modu');
    window.print();
    setTimeout(() => document.body.classList.remove('rapor-yazdir-modu'), 1000);
  };

  const n = (v: any) => Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 });

  // Mevcut ırk listesi - seçilen konulara göre
  const mevcutIrklar: Record<string,string[]> = {};
  secilenKonular.forEach(k => { if (IRK_LISTESI[k]) mevcutIrklar[k] = IRK_LISTESI[k]; });
  if (secilenKonular.includes('hayvancilik') || secilenKonular.includes('bitkisel')) {
    ['buyukbas','kucukbas','tarla'].forEach(k => { if (IRK_LISTESI[k]) mevcutIrklar[k] = IRK_LISTESI[k]; });
  }

  const yilSecenekleri = [2020,2021,2022,2023,2024,2025,2026];

  return (
    <div className="rapor-container" style={{ display: 'flex', gap: 12, height: 'calc(100vh - 80px)' }}>
    <style>{`
      @media print {
        body.rapor-yazdir-modu .topbar,
        body.rapor-yazdir-modu .navbar,
        body.rapor-yazdir-modu .subbar,
        body.rapor-yazdir-modu .sidebar,
        body.rapor-yazdir-modu .rapor-sol-panel { display: none !important; }
        body.rapor-yazdir-modu .rapor-container { height: auto !important; display: block !important; }
        body.rapor-yazdir-modu .rapor-sag-panel {
          width: 100% !important; height: auto !important;
          overflow: visible !important; box-shadow: none !important;
          border-radius: 0 !important;
        }
        body.rapor-yazdir-modu table { page-break-inside: auto; }
        body.rapor-yazdir-modu tr { page-break-inside: avoid; }
      }
    `}</style>

      {/* SOL PANEL */}
      <div className="rapor-sol-panel" style={{ width: 300, background: 'white', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#1a5276', color: 'white', padding: '12px 16px', fontSize: 15, fontWeight: 'bold', flexShrink: 0 }}>
          📊 Rapor Kriterleri
        </div>

        {/* 1. KAPSAM */}
        <div style={{ padding: '12px 16px', borderBottom: '2px solid #eee' }}>
          <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1a5276', marginBottom: 10, textTransform: 'uppercase' }}>1. Kapsam</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
            <input type="radio" checked={tumIsletmeler} onChange={() => { setTumIsletmeler(true); setSecilenIsletmeler([]); }} />
            <strong>Genel Müdürlük (Tümü)</strong>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8, cursor: 'pointer' }}>
            <input type="radio" checked={!tumIsletmeler} onChange={() => setTumIsletmeler(false)} />
            İşletme Seçimi
          </label>
          {!tumIsletmeler && (
            <div style={{ marginLeft: 20, maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4, cursor: 'pointer', fontWeight: 'bold' }}>
                <input type="checkbox"
                  checked={secilenIsletmeler.length === isletmeler.length}
                  onChange={() => secilenIsletmeler.length === isletmeler.length
                    ? setSecilenIsletmeler([])
                    : setSecilenIsletmeler(isletmeler.map(i => i.id))} />
                Tümünü Seç
              </label>
              {isletmeler.map(i => (
                <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 3, cursor: 'pointer' }}>
                  <input type="checkbox" checked={secilenIsletmeler.includes(i.id)} onChange={() => toggleIsletme(i.id)} />
                  <strong>{i.kod}</strong> — {i.ad.replace(' Tarım İşletmesi','')}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* 2. DÖNEM */}
        <div style={{ padding: '12px 16px', borderBottom: '2px solid #eee' }}>
          <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1a5276', marginBottom: 10, textTransform: 'uppercase' }}>2. Dönem</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>Yıl Seçimi (birden fazla = kıyaslama)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {yilSecenekleri.map(y => (
              <label key={y} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer',
                background: yillar.includes(y) ? '#1a5276' : '#f0f0f0',
                color: yillar.includes(y) ? 'white' : '#333',
                padding: '4px 10px', borderRadius: 4 }}>
                <input type="checkbox" checked={yillar.includes(y)} onChange={() => toggleYil(y)} style={{ display: 'none' }} />
                {y}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#555' }}>Ay:</div>
            <select value={ay} onChange={e => setAy(Number(e.target.value))}
              style={{ flex: 1, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}>
              <option value={0}>Tüm Yıl</option>
              {AYLAR.slice(1).map((a, i) => <option key={i+1} value={i+1}>{a}</option>)}
            </select>
          </div>
          {yillar.length > 1 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 8, cursor: 'pointer',
              background: kiyaslama ? '#eafaf1' : '#f9f9f9', padding: '6px 8px', borderRadius: 4,
              border: `1px solid ${kiyaslama ? '#27ae60' : '#ddd'}` }}>
              <input type="checkbox" checked={kiyaslama} onChange={e => setKiyaslama(e.target.checked)} />
              <span>📊 Kıyaslama Modu — Yılları yanyana göster</span>
            </label>
          )}
        </div>

        {/* 3. KONU */}
        <div style={{ padding: '12px 16px', borderBottom: '2px solid #eee', overflow: 'auto', flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1a5276', marginBottom: 10, textTransform: 'uppercase' }}>3. Konu</div>
          {KONU_AGACI.map(ana => (
            <div key={ana.id} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => setAcikDallar(prev => prev.includes(ana.id) ? prev.filter(x => x !== ana.id) : [...prev, ana.id])}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#aaa', padding: 0, width: 14 }}>
                  {acikDallar.includes(ana.id) ? '▼' : '▶'}
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', fontWeight: 'bold', color: '#1a5276' }}>
                  <input type="checkbox" checked={secilenKonular.includes(ana.id)} onChange={() => toggleKonu(ana.id)} />
                  {ana.ikon} {ana.ad}
                </label>
              </div>
              {acikDallar.includes(ana.id) && (
                <div style={{ marginLeft: 22, marginTop: 4 }}>
                  {ana.children.map((alt: any) => (
                    <label key={alt.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4, cursor: 'pointer' }}>
                      <input type="checkbox"
                        checked={secilenKonular.includes(alt.id) || secilenKonular.includes(ana.id)}
                        onChange={() => toggleKonu(alt.id, ana.id)} />
                      {alt.ikon} {alt.ad}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 4. IRKA/ÜRÜNE GÖRE FİLTRE */}
        {Object.keys(mevcutIrklar).length > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '2px solid #eee' }}>
            <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1a5276', marginBottom: 8, textTransform: 'uppercase' }}>4. Irk / Ürün Filtresi</div>
            <input placeholder="Ara..." value={irkArama} onChange={e => setIrkArama(e.target.value)}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, marginBottom: 6, boxSizing: 'border-box' as const }} />
            <div style={{ maxHeight: 160, overflow: 'auto' }}>
              {Object.entries(mevcutIrklar).map(([alan, irklar]) => (
                <div key={alan} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 3, textTransform: 'uppercase' }}>{alan}</div>
                  {(irklar as string[]).filter(i => i.toLowerCase().includes(irkArama.toLowerCase())).map(irk => (
                    <label key={irk} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 3, cursor: 'pointer' }}>
                      <input type="checkbox" checked={secilenIrklar.includes(irk)} onChange={() => toggleIrk(irk)} />
                      {irk}
                    </label>
                  ))}
                </div>
              ))}
            </div>
            {secilenIrklar.length > 0 && (
              <div style={{ fontSize: 12, color: '#27ae60', marginTop: 4 }}>
                {secilenIrklar.length} seçili
                <button onClick={() => setSecilenIrklar([])} style={{ marginLeft: 6, background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 12 }}>Temizle</button>
              </div>
            )}
          </div>
        )}

        {/* RAPOR BUTONU */}
        <div style={{ padding: 12, flexShrink: 0 }}>
          <button className="btn btn-primary" style={{ width: '100%', marginBottom: 8, fontSize: 15, padding: '10px' }}
            onClick={raporOlustur} disabled={yukleniyor}>
            {yukleniyor ? '⏳ Hazırlanıyor...' : '📊 Rapor Hazırla'}
          </button>
          {rapor && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn" style={{ flex: 1, background: '#27ae60', color: 'white', fontSize: 13 }} onClick={excelIndir}>
                📥 Excel
              </button>
              <button className="btn" style={{ flex: 1, background: '#c0392b', color: 'white', fontSize: 13 }} onClick={pdfIndir}>
                🖨️ PDF Kaydet
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SAĞ PANEL - Rapor */}
      <div className="rapor-sag-panel" style={{ flex: 1, background: 'white', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'auto' }} ref={raporRef}>
        {!rapor && !yukleniyor && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📊</div>
            <div style={{ fontSize: 16 }}>Soldan kapsam ve dönem seçimi yapın</div>
            <div style={{ fontSize: 14, marginTop: 8, color: '#bbb' }}>Kriterler seçilince rapor otomatik güncellenir</div>
          </div>
        )}

        {yukleniyor && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ textAlign: 'center', color: '#888' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
              <div style={{ fontSize: 16 }}>Rapor hazırlanıyor...</div>
            </div>
          </div>
        )}

        {rapor && (
          <div style={{ padding: 24 }} id="rapor-icerik">
            {/* Rapor başlığı */}
            <div style={{ borderBottom: '3px solid #1a5276', paddingBottom: 14, marginBottom: 24 }}>
              <h2 style={{ color: '#1a5276', margin: 0, fontSize: 22 }}>TİGEM Faaliyet Raporu</h2>
              <div style={{ fontSize: 14, color: '#888', marginTop: 6 }}>
                {rapor.yillar.join(', ')} yılı •
                {rapor.ay ? ` ${AYLAR[rapor.ay]} ayı •` : ' Yıllık •'}
                {tumIsletmeler ? ' Tüm İşletmeler' : ` ${secilenIsletmeler.length} işletme`}
                {rapor.kiyaslama_modu && ' • 📊 Kıyaslama Modu'}
                {secilenIrklar.length > 0 && ` • Filtre: ${secilenIrklar.join(', ')}`}
                {' • '}{new Date().toLocaleDateString('tr-TR')}
              </div>
            </div>

            {rapor.bolumler.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: 48, fontSize: 15 }}>
                Seçilen kriterlere uygun veri bulunamadı.<br/>
                <span style={{ fontSize: 13 }}>Farklı yıl veya konu seçin.</span>
              </div>
            )}

            {rapor.bolumler.map((bolum: any, bi: number) => (
              <div key={bi} style={{ marginBottom: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #eaf0fb', paddingBottom: 8, marginBottom: 14 }}>
                  <h3 style={{ fontSize: 17, color: '#1a5276', margin: 0 }}>{bolum.baslik}</h3>
                  {bolum.ozet && Object.keys(bolum.ozet).length > 0 && (
                    <div style={{ display: 'flex', gap: 16 }}>
                      {Object.entries(bolum.ozet).map(([k, v]: any) => (
                        <div key={k} style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a5276' }}>{n(v)}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{k.replace(/_/g,' ')}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#1a5276', color: 'white' }}>
                        {bolum.sutunlar.map((s: string, si: number) => (
                          <th key={si} style={{ padding: '8px 12px', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 13 }}>{s}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bolum.satirlar.map((satir: any, ri: number) => (
                        <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#f8f9fa' }}>
                          {Object.values(satir).map((v: any, vi: number) => (
                            <td key={vi} style={{ padding: '7px 12px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', fontSize: 13 }}>
                              {typeof v === 'number' ? n(v) : v}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    {bolum.satirlar.length > 1 && (
                      <tfoot>
                        <tr style={{ background: '#eaf0fb', fontWeight: 'bold' }}>
                          {bolum.sutunlar.map((_: any, si: number) => {
                            if (si === 0) return <td key={si} style={{ padding: '7px 12px', fontSize: 13 }}>TOPLAM</td>;
                            const vals = bolum.satirlar.map((s: any) => Object.values(s)[si]);
                            const sum = vals.reduce((a: any, b: any) => typeof b === 'number' ? a + b : a, 0);
                            return <td key={si} style={{ padding: '7px 12px', fontSize: 13 }}>{typeof vals[0] === 'number' ? n(sum) : ''}</td>;
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
